/**
 * Pack-schema migrations (R13) — the passage from an older authored document
 * to the schema of NOW, walked before validation ever sees it.
 *
 * @remarks
 * The creator-studio truth this exists for: an author's pack saved in March
 * must open in the June schema. `content-schema` stays the truth of the
 * PRESENT (exact-match `schemaVersion`, unchanged); this registry is the
 * loader's ownership of the PAST — the same division of labor the save
 * registry ruled in R10, whose machinery has now paid for itself three
 * times ('1'→'2'→'3' in two rounds, and the parse-ordering defect it
 * surfaced).
 *
 * The shape mirrors `engine-runtime/saves` deliberately, with the two
 * differences the artifact forces:
 *
 * - **Steps operate on the RAW DOCUMENT** (`unknown`): an older pack's
 *   shape is, by definition, not the current schema, so it cannot be typed
 *   until the walk is done. Each step is the one place that knows its
 *   source shape.
 * - **No checksum to re-seal** — packs are authored artifacts, not
 *   integrity-sealed snapshots — so each step MUST re-stamp
 *   `manifest.schemaVersion` itself; the walk trusts the stamp to select
 *   the next step (or to stop).
 *
 * A document the walk cannot read (no manifest, wrong shape) passes through
 * UNTOUCHED: schema validation is the airlock's error surface, and it
 * already reports malformed documents and unsupported versions precisely —
 * this module never invents a second way to fail.
 *
 * The registry shipped EMPTY through `0.1.0` — the first content-schema
 * version, with nothing to migrate FROM — and the walking machinery was
 * tested against a synthetic step so that the first real migration could
 * land as DATA with before/after fixtures. D158 is that migration.
 *
 * @packageDocumentation
 */

import type { ConditionDefinition, NarrativeLine } from '@orpheus/content-schema';

/** One migration step: rewrites a raw pack document FROM the named schema version. */
export interface PackMigration {
	/** The `manifest.schemaVersion` this step consumes. */
	readonly fromVersion: string;
	/**
	 * The pure rewrite; it MUST re-stamp `manifest.schemaVersion` with the
	 * version it produced (the walk reads the stamp to select the next step).
	 */
	readonly migrate: (olderDocument: unknown) => unknown;
}

/**
 * D158 — `exitLock` becomes a condition and a conditional refusal.
 *
 * @remarks
 * Four fields retire and two arrive: `lockedFlag` becomes a `flag_equals`
 * condition, `lockedNarratives` becomes the refusal, and the
 * `keyItemId` + `lockedWithKeyItemNarratives` pair becomes one `has_item`
 * variant in front of it. Everything the old shape could say, the new shape
 * says; nothing the author wrote is dropped, and no line's TEXT is touched.
 *
 * One case does not translate and must not: `lockedFlag` on an outward exit.
 * An outward exit refuses BEFORE its lock is engaged (D19a), so the condition
 * was never evaluated and the 0.2.0 schema now refuses one there. The step
 * drops it and keeps the refusal, which is what the flag had always meant in
 * practice — measured, not assumed: the POC's only `lockedFlag` was exactly
 * this shape and had never once executed.
 *
 * @param olderDocument - A raw 0.1.0 pack document
 * @returns The same document at 0.2.0
 */
const migrateExitLocksToConditions = (olderDocument: unknown): unknown => {
	const document = olderDocument as {
		manifest?: Record<string, unknown>;
		rooms?: { exits?: MigratableExit[] }[];
	};

	for (const room of document.rooms ?? []) {
		for (const exit of room.exits ?? []) {
			if (exit.lock !== undefined) {
				exit.lock = migratedLock(exit.lock as LegacyExitLock, exit.leadsOutOfPack === true);
			}
		}
	}

	return {
		...document,
		manifest: { ...document.manifest, schemaVersion: '0.2.0' },
	};
};

/** One exit mid-walk: a 0.1.0 lock on the way in, a 0.2.0 lock on the way out. */
interface MigratableExit {
	leadsOutOfPack?: boolean;
	lock?: LegacyExitLock | MigratedExitLock;
}

/** The 0.2.0 lock this step writes. */
interface MigratedExitLock {
	lockedWhen?: ConditionDefinition;
	refusal: unknown;
}

/** One 0.1.0 exit lock — the four retired fields. */
interface LegacyExitLock {
	keyItemId?: string;
	lockedFlag?: string;
	lockedNarratives?: readonly NarrativeLine[];
	lockedWithKeyItemNarratives?: readonly NarrativeLine[];
}

/** The 0.2.0 shape of one 0.1.0 lock. */
const migratedLock = (legacyLock: LegacyExitLock, isOutward: boolean): MigratedExitLock => {
	const fallback = legacyLock.lockedNarratives ?? [];
	const refusal =
		legacyLock.keyItemId === undefined || legacyLock.lockedWithKeyItemNarratives === undefined
			? fallback
			: {
				fallback,
				variants: [{ narratives: legacyLock.lockedWithKeyItemNarratives, when: { itemId: legacyLock.keyItemId, type: 'has_item' }}],
			};

	// The condition is dropped on an outward exit rather than translated: the
	// 0.2.0 schema refuses one there, and it could never have been evaluated.
	return legacyLock.lockedFlag === undefined || isOutward
		? { refusal }
		: { lockedWhen: { expectedValue: true, flagId: legacyLock.lockedFlag, type: 'flag_equals' } as ConditionDefinition, refusal };
};

/**
 * D159: `openable.openDescription` / `closedDescription` fold into the
 * conditional description they always desugared to (0.2.0 → 0.3.0).
 *
 * @remarks
 * The engine translated the pair into two leading variants —
 * `container_open` for the open reading, its negation for the shut one —
 * ahead of the authored ones, and resolved by the primitive. This step
 * writes exactly that translation into the document, in that order, so a
 * migrated pack reads precisely as it read before: **nothing an author
 * wrote is lost, and nothing that was unreachable becomes reachable.**
 * That last clause is a finding, not a comfort: with BOTH readings
 * declared, the item's plain `description` could never speak (one of the
 * two variants always holds), and it still cannot — it is now visibly the
 * `fallback` under two variants that cover it, where a reader can see it
 * and decide. One case IS decided here, because it has only one honest
 * answer: a reading that is byte-for-byte the fallback is the fallback,
 * and writing it as a variant too would say one thing twice (D140's gate
 * would name it the moment it landed). Such a reading contributes no
 * variant; its text is kept once, where it always spoke from.
 */
const migrateOpenableDescriptionsToVariants = (olderDocument: unknown): unknown => {
	const document = olderDocument as { items?: MigratableItem[]; manifest?: Record<string, unknown> };

	for (const item of document.items ?? []) {
		if (item.openable === undefined) {
			continue;
		}

		const { closedDescription, openDescription, ...openable } = item.openable;
		const authored = typeof item.description === 'string' ? { fallback: item.description, variants: []} : item.description;
		const isOpen = { itemId: item.itemId, type: 'container_open' } as const;
		const readings: MigratedVariant[] = [
			...(openDescription === undefined ? [] : [{ condition: isOpen, text: openDescription }]),
			...(closedDescription === undefined ? [] : [{ condition: { condition: isOpen, type: 'not' } as const, text: closedDescription }]),
		];
		const leading = readings.filter((reading) => reading.text !== authored?.fallback);

		item.openable = openable;

		if (leading.length > 0 && authored !== undefined) {
			item.description = { fallback: authored.fallback, variants: [...leading, ...authored.variants]};
		}
	}

	return {
		...document,
		manifest: { ...document.manifest, schemaVersion: '0.3.0' },
	};
};

/** One 0.3.0 description variant as this step writes it — the condition raw, since the document is unvalidated here. */
interface MigratedVariant {
	condition: unknown;
	text: string;
}

/** One item mid-walk: the 0.2.0 pair on the way in, the variants on the way out. */
interface MigratableItem {
	description?: string | { fallback: string; variants: MigratedVariant[] };
	itemId: string;
	openable?: { closedDescription?: string; openDescription?: string } & Record<string, unknown>;
}

/** The engine's four fixed sources, as 0.4.0 fixed them — anything else a 0.3.0 line spoke in is a speaker. */
const FIXED_SOURCES: ReadonlySet<string> = new Set(['environment', 'human_log', 'player', 'system']);

/**
 * The one speaker every 0.3.0 pack could have had, declared as the POC
 * declares her: quoted (D84), the metronome (D99), the shell's voice tint.
 */
const EURYDICE_DECLARATION = {
	color: '#9fd4c4',
	isQuoted: true,
	name: 'EURYDICE',
	reveal: { cadence: 'stepped', stepMs: 14 },
	speakerId: 'eurydice',
} as const;

/**
 * Every `source` value a document's lines use, wherever they sit, in first-
 * seen order — the structural walk the quotation law uses, so a source in a
 * voice pool, a reaction, a paywall line or an override counts the same.
 */
const collectSources = (node: unknown, found: string[] = []): string[] => {
	if (Array.isArray(node)) {
		node.forEach((element) => collectSources(element, found));

		return found;
	}

	if (typeof node !== 'object' || node === null) {
		return found;
	}

	const record = node as Readonly<Record<string, unknown>>;
	const source = record['source'];

	if (typeof source === 'string' && !FIXED_SOURCES.has(source) && !found.includes(source)) {
		found.push(source);
	}

	for (const value of Object.values(record)) {
		collectSources(value, found);
	}

	return found;
};

/**
 * D192: the narrator becomes content (0.3.0 → 0.4.0). A 0.3.0 line's
 * `source` was one of five enum members, one of them the POC's character;
 * a 0.4.0 line's is a fixed kind or a speaker the pack declares. Every
 * speaker id the document's lines use is declared — `eurydice` as the POC
 * declares her, any other as itself, unquoted, named by its own id (never
 * uppercased: capitals are the shout's, Calder's `who-said-that` §4.2) —
 * and no line is touched. A document whose lines use only the
 * fixed four declares nothing (Mike: speakers are optional).
 */
const migrateNarratorToSpeakers = (olderDocument: unknown): unknown => {
	const document = olderDocument as { manifest?: Record<string, unknown>; speakers?: unknown };
	const speakers = collectSources(document).map((speakerId) =>
		speakerId === EURYDICE_DECLARATION.speakerId ? EURYDICE_DECLARATION : { isQuoted: false, name: speakerId, speakerId },
	);

	return {
		...document,
		manifest: { ...document.manifest, schemaVersion: '0.4.0' },
		...(speakers.length === 0 ? {} : { speakers }),
	};
};

/** A 0.4.0 map block, as far as the step reads it: the two style fields, and whatever else it carries untouched. */
interface LegacyMapBlock {
	readonly [key: string]: unknown;
	readonly style?: unknown;
	readonly styles?: unknown;
}

/** The one map style 0.4.0 offered that 0.5.0 does not, and the one it reads as. */
const HOLOGRAM_STYLE = 'holo';
const HOLOGRAM_READS_AS = 'scene';

/**
 * D215: the hologram is the fallback (0.4.0 → 0.5.0). A 0.4.0 pack could
 * open on the hologram or offer it beside the plan and the scene; since
 * D215 (Mike, 2026-09-27) the hologram is only what `scene` shows where the
 * browser cannot draw, and a builder chooses between `basic` and `scene`.
 * The step reads `holo` as `scene` — in `map.style`, and in `map.styles`
 * once, in the hologram's place, so an offer of `['basic', 'holo']` becomes
 * `['basic', 'scene']` and `['holo', 'scene']` collapses to `['scene']`. A
 * pack that never named the hologram is re-stamped and otherwise untouched.
 */
const migrateHologramToScene = (olderDocument: unknown): unknown => {
	const document = olderDocument as { manifest?: { presentation?: { map?: LegacyMapBlock } & Record<string, unknown> } & Record<string, unknown> };
	const map = document.manifest?.presentation?.map;
	const readStyle = (style: unknown): unknown => (style === HOLOGRAM_STYLE ? HOLOGRAM_READS_AS : style);
	const migratedMap =
		map === undefined
			? undefined
			: {
				...map,
				...(map.style === undefined ? {} : { style: readStyle(map.style) }),
				...(Array.isArray(map.styles) ? { styles: [...new Set(map.styles.map(readStyle))]} : {}),
			};

	return {
		...document,
		manifest: {
			...document.manifest,
			schemaVersion: '0.5.0',
			...(migratedMap === undefined ? {} : { presentation: { ...document.manifest?.presentation, map: migratedMap }}),
		},
	};
};

/** The registry, in walking order — every entry ships with before/after fixtures. */
export const PACK_MIGRATIONS: readonly PackMigration[] = [
	{ fromVersion: '0.1.0', migrate: migrateExitLocksToConditions },
	{ fromVersion: '0.2.0', migrate: migrateOpenableDescriptionsToVariants },
	{ fromVersion: '0.3.0', migrate: migrateNarratorToSpeakers },
	{ fromVersion: '0.4.0', migrate: migrateHologramToScene },
];

/**
 * Walk a raw pack document forward through every applicable migration.
 *
 * @param rawDocument - The acquired document, exactly as the source produced it
 * @param migrations - The registry (injectable for the machinery tests)
 * @returns The document after every applicable step (the SAME reference when none apply)
 */
export const walkPackMigrations = (
	rawDocument: unknown,
	migrations: readonly PackMigration[] = PACK_MIGRATIONS,
): unknown => {
	let workingDocument = rawDocument;

	for (const migration of migrations) {
		if (declaredSchemaVersionOf(workingDocument) === migration.fromVersion) {
			workingDocument = migration.migrate(workingDocument);
		}
	}

	return workingDocument;
};

/**
 * The `manifest.schemaVersion` a raw document declares, when it is readable.
 *
 * @remarks
 * Deliberately forgiving: a document without a readable version simply
 * matches no step, and schema validation downstream reports what is actually
 * wrong with it.
 *
 * @param rawDocument - The unvalidated document
 * @returns The declared version, or `undefined`
 */
const declaredSchemaVersionOf = (rawDocument: unknown): string | undefined => {
	if (typeof rawDocument !== 'object' || rawDocument === null) {
		return undefined;
	}

	const manifest = (rawDocument as { manifest?: unknown }).manifest;

	if (typeof manifest !== 'object' || manifest === null) {
		return undefined;
	}

	const declaredVersion = (manifest as { schemaVersion?: unknown }).schemaVersion;

	return typeof declaredVersion === 'string' ? declaredVersion : undefined;
};
